// @erp/module-cms/schemas — the EVENTS content type wire contracts. A calendar
// entry (a university event, trial, tournament, or convocation): a titled event
// with a display DATE, a venue + address, a banners[] image list, an
// attachments[] document list, a daily-agenda schedule, ticketing, a featured
// flag, and a companion-announcement auto-sync (type='event'). Modelled on the
// 0005 tenders slice (the richest companion-sync precedent) minus the ref-no
// engine + corrigendum linkage. Browser-safe: zod + plain data only (fe forms and
// the gateway parse the SAME schemas). Feature-referenced from MBSPSU (READ-ONLY)
// — rebuilt for the target stack, English-first + locale-ready (a translation is a
// SIBLING ROW under a different `locale`, never a *_hi/*_pa column explosion).
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ SECURITY — body_html is stored RAW and is NOT sanitized on write (the 0004     │
// │ announcements law). Sanitization happens ON THE WAY OUT: every /public/*        │
// │ route passes bodyHtml through the allowlist sanitiser (api/sanitize.ts,         │
// │ wired 2026-09-13 per ADR-010 §2.4) before it leaves the ERP. Admin reads        │
// │ carry the raw column; content is authored ONLY by trusted cms:events holders.   │
// ├──────────────────────────────────────────────────────────────────────────────┤
// │ DEFERRED (documented, NOT built): a private/PIN + HMAC signed-asset gate is a   │
// │ CROSS-CUTTING "private records" slice tenders/events/publications will share —  │
// │ so there is NO privatePin / visibilitySchedule here and status is               │
// │ draft|published|archived only (no 'private' yet). Revisions + the public read-  │
// │ split are likewise deferred to their own waves.                                 │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";
import { HttpUrl } from "./href.js";
import { CursorQuery, EntityId, EpochMs, Paise } from "@erp/core-schemas";
import { SLUG_MAX, SLUG_RE } from "./categories.js";
import { Locale } from "./locale.js";
import { AnnouncementAttachment, ATTACHMENTS_MAX, BODY_HTML_MAX, SUMMARY_MAX } from "./announcements.js";
import { PinField, RecordAccessLevel } from "./private.js";
import { PublishingStatus } from "./settings.js";
import { Highlight, Highlights, Tags } from "./structured.js";

// NOTE: slugify + SLUG_MAX/SLUG_RE are REUSED from the categories slice (the ONE slug
// grammar + deriver) and BODY_HTML_MAX / the AnnouncementAttachment shape from the
// announcements slice — one source, never a re-declaration.

// ── The publishing status enum — REUSED from settings (draft|published|archived),
// so the event lifecycle and the site-wide default_publishing_status can never drift
// onto different value sets (the announcements/tenders precedent). ────────────────────
export const EventStatus = PublishingStatus;
export type EventStatus = z.infer<typeof EventStatus>;

// ── The display DATE, DERIVED at read from the stored instant (never three stored
// strings that can drift). event_start is one epoch-ms UTC instant; the day / month /
// year / weekday are computed from it. PURE + browser-safe so the admin form can
// PREVIEW the same breakdown the read surfaces. UTC — a Worker runs in UTC; tests pin
// a fixed epoch. ──────────────────────────────────────────────────────────────────────
export const EventDatePartsSchema = z.object({
  /** Zero-padded day of month, e.g. "23". */
  day: z.string(),
  /** Full month name, e.g. "June". */
  month: z.string(),
  /** Four-digit year, e.g. "2026". */
  year: z.string(),
  /** Full weekday name, e.g. "Tuesday". */
  weekday: z.string(),
});
export type EventDateParts = z.infer<typeof EventDatePartsSchema>;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;
const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
] as const;

/** Derive the day/month/year/weekday breakdown from a stored instant (UTC); null when the instant is absent/invalid. */
export const deriveEventDateParts = (at: number | null | undefined): EventDateParts | null => {
  if (at == null) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  // getUTCMonth() ∈ 0..11 and getUTCDay() ∈ 0..6 always index in bounds; the non-null
  // assertions satisfy noUncheckedIndexedAccess without a spurious runtime fallback.
  return {
    day: String(d.getUTCDate()).padStart(2, "0"),
    month: MONTH_NAMES[d.getUTCMonth()]!,
    year: String(d.getUTCFullYear()),
    weekday: WEEKDAY_NAMES[d.getUTCDay()]!,
  };
};

// ══════════════════ Structured sub-objects (serialized to *_json TEXT columns) ══════════════════
// Each is a zod object/array stored as one nullable TEXT column. Kept bounded (bounded
// strings, integer paise) so a runaway blob can never bloat the row. Inner fields are
// mostly optional (a partially-filled event form is valid).

/** One postal address (the event venue's structured location; venue is the scalar name). */
export const EventAddress = z.object({
  line1: z.string().trim().max(300).optional(),
  line2: z.string().trim().max(300).optional(),
  city: z.string().trim().max(120).optional(),
  state: z.string().trim().max(120).optional(),
  pinCode: z.string().trim().max(20).optional(),
  country: z.string().trim().max(120).optional(),
});
export type EventAddress = z.infer<typeof EventAddress>;

/** A hero/banner image reference (a media URL + display alt/caption). The URL rides an <img src>, never raw HTML — an absolute http(s) URL (schemas/href.ts HttpUrl, SEC-5). */
export const EventBanner = z.object({
  url: HttpUrl,
  alt: z.string().trim().max(300).optional(),
  caption: z.string().trim().max(500).optional(),
});
export type EventBanner = z.infer<typeof EventBanner>;
export const BANNERS_MAX = 30;

/** One daily-agenda row — an optional instant (its own day/time derives at read) + display fields. */
export const EventScheduleItem = z.object({
  /** The agenda item's instant (epoch-ms UTC) — the day/time can be derived at read. */
  date: EpochMs.optional(),
  title: z.string().trim().min(1).max(300),
  /** Display start/end time (free text, e.g. "10:00 AM") — a label, not an instant. */
  startTime: z.string().trim().max(50).optional(),
  endTime: z.string().trim().max(50).optional(),
  venue: z.string().trim().max(300).optional(),
  description: z.string().trim().max(2000).optional(),
});
export type EventScheduleItem = z.infer<typeof EventScheduleItem>;
export const SCHEDULE_MAX = 100;

/** Ticketing — capacity + PRICE IN INTEGER PAISE (never a float; the db-lib money law) + a registration link. */
export const EventTickets = z.object({
  /** Total capacity (a non-negative integer count). */
  total: z.number().int().min(0).optional(),
  /** Ticket price in INTEGER PAISE (Paise rejects floats + negatives). */
  pricePaise: Paise.optional(),
  /** An external registration URL. */
  registrationLink: z.string().trim().min(1).max(2000).optional(),
});
export type EventTickets = z.infer<typeof EventTickets>;

// ── The write-side field validators (one source, Create/Update both derive) ─────────
const eventCore = {
  title: z.string().trim().min(1).max(300),
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  /** RAW HTML — sanitized ONLY at the website render (HTMLRewriter), never here. Nullable to clear. */
  bodyHtml: z.string().max(BODY_HTML_MAX).nullable(),
  summary: z.string().trim().max(SUMMARY_MAX).nullable(),
  status: EventStatus,
  /** Explicit publish instant (epoch-ms). Usually left unset — the service stamps it when status→published. Nullable to clear. */
  publishedAt: EpochMs.nullable(),
  /** FK to a cms_category of type='event' — the service validates the cross-entity ref. Nullable to clear. */
  categoryId: EntityId.nullable(),
  /** The event instant(s) — one stored instant each; the display date derives at read. Nullable. */
  eventStart: EpochMs.nullable(),
  eventEnd: EpochMs.nullable(),
  /** The scalar venue/location NAME; the structured postal address rides `address`. Nullable to clear. */
  venue: z.string().trim().max(300).nullable(),
  address: EventAddress.nullable(),
  banners: z.array(EventBanner).max(BANNERS_MAX).nullable(),
  attachments: z.array(AnnouncementAttachment).max(ATTACHMENTS_MAX).nullable(),
  schedule: z.array(EventScheduleItem).max(SCHEDULE_MAX).nullable(),
  tickets: EventTickets.nullable(),
  /** Structured "Key Highlights" (0018, A3 — schemas/structured.ts Highlight, ≤30). Nullable to clear. */
  highlights: Highlights.nullable(),
  /** Bare-string tags (0018, A4 — the gallery rule: ≤60 chars, ≤50 per row). Nullable to clear. */
  tags: Tags.nullable(),
  /** Surface in the featured rail. */
  featured: z.boolean(),
  /** Locale-ready seam — v1 writes only 'en' (db-cms 0006). */
  /** The row's locale — a canonical BCP 47 tag (./locale.ts, the locale contract). */
  locale: Locale,
};

/**
 * Create body — title required; slug OPTIONAL (the service derives it via slugify(title),
 * id-fallback on a non-sluggable title). status/locale/featured carry the DDL defaults.
 * createAnnouncement is a ROUTING flag (not a column) — publish auto-posts a companion
 * announcement unless it is false (the tenders precedent; default on).
 */
export const CreateCmsEvent = z.object({
  title: eventCore.title,
  slug: eventCore.slug.optional(),
  bodyHtml: eventCore.bodyHtml.optional(),
  summary: eventCore.summary.optional(),
  status: eventCore.status.default("draft"),
  publishedAt: eventCore.publishedAt.optional(),
  categoryId: eventCore.categoryId.optional(),
  eventStart: eventCore.eventStart.optional(),
  eventEnd: eventCore.eventEnd.optional(),
  venue: eventCore.venue.optional(),
  address: eventCore.address.optional(),
  banners: eventCore.banners.optional(),
  attachments: eventCore.attachments.optional(),
  schedule: eventCore.schedule.optional(),
  tickets: eventCore.tickets.optional(),
  highlights: eventCore.highlights.optional(),
  tags: eventCore.tags.optional(),
  featured: eventCore.featured.default(false),
  locale: eventCore.locale.default("en"),
  createAnnouncement: z.boolean().optional(),
  // Private-records slice (0011): access_level + the WRITE-ONLY pin (see tenders.ts).
  accessLevel: RecordAccessLevel.default("public"),
  pin: PinField.optional(),
});
export type CreateCmsEvent = z.infer<typeof CreateCmsEvent>;

/** Update body — a partial PATCH over the same fields (slug stays STABLE on rename; a slug collision still maps to 409). */
export const UpdateCmsEvent = z
  .object({
    title: eventCore.title,
    slug: eventCore.slug,
    bodyHtml: eventCore.bodyHtml,
    summary: eventCore.summary,
    status: eventCore.status,
    publishedAt: eventCore.publishedAt,
    categoryId: eventCore.categoryId,
    eventStart: eventCore.eventStart,
    eventEnd: eventCore.eventEnd,
    venue: eventCore.venue,
    address: eventCore.address,
    banners: eventCore.banners,
    attachments: eventCore.attachments,
    schedule: eventCore.schedule,
    tickets: eventCore.tickets,
    highlights: eventCore.highlights,
    tags: eventCore.tags,
    featured: eventCore.featured,
    locale: eventCore.locale,
    createAnnouncement: z.boolean(),
    // Private-records slice (0011): flip access_level / (re)set the write-only pin (tenders.ts).
    accessLevel: RecordAccessLevel,
    pin: PinField,
  })
  .partial();
export type UpdateCmsEvent = z.infer<typeof UpdateCmsEvent>;

/**
 * List query — keyset cursor+limit + optional ?status ?categoryId ?featured ?upcoming ?locale
 * filters (soft-deleted rows always excluded). ?featured "1"/"0" is coerced to the stored
 * INTEGER; ?upcoming "true" narrows to events whose event_start is at/after the request now.
 */
export const CmsEventListQuery = CursorQuery.extend({
  status: EventStatus.optional(),
  categoryId: EntityId.optional(),
  /** Exact-eq on the row's locale (0018) — the admin list narrows to one locale's rows; omitted lists every locale. */
  locale: Locale.optional(),
  /** "1" featured / "0" ordinary — coerced to the stored INTEGER on the way to SQL. */
  featured: z.enum(["0", "1"]).optional(),
  /** "true" → only events at/after now (event_start >= now); "false"/absent → no upcoming filter. */
  upcoming: z.enum(["true", "false"]).optional(),
  /** Opt-in TRASH view — true lists soft-deleted rows only; omitted (the default) lists live rows. Coerced + optional so a stray value never 422s the query. */
  // Self-healing like the FE's ?trashed: only "1"/"true" opt into trash; anything else
  // (absent, "false", "0", garbage) heals to the live list — never flips to trash or 422s.
  trashed: z
    .enum(["1", "true"])
    .transform(() => true)
    .optional()
    .catch(undefined),
});
export type CmsEventListQuery = z.infer<typeof CmsEventListQuery>;

/**
 * The wire DTO — the read shape (camelCase; audit actor columns stay server-side).
 * The structured blobs surface as parsed objects/lists; `startParts` is the DERIVED
 * day/month/year/weekday of event_start (not a stored column); `deletedAt` surfaces so
 * the admin trash view can tell live rows from trashed ones.
 */
export const CmsEvent = z.object({
  id: EntityId,
  slug: z.string(),
  locale: z.string(),
  title: z.string(),
  bodyHtml: z.string().nullable(),
  summary: z.string().nullable(),
  status: EventStatus,
  publishedAt: EpochMs.nullable(),
  categoryId: EntityId.nullable(),
  eventStart: EpochMs.nullable(),
  eventEnd: EpochMs.nullable(),
  startParts: EventDatePartsSchema.nullable(),
  venue: z.string().nullable(),
  address: EventAddress.nullable(),
  banners: z.array(EventBanner),
  attachments: z.array(AnnouncementAttachment),
  schedule: z.array(EventScheduleItem),
  tickets: EventTickets.nullable(),
  /** 0018 — the parsed highlights list ([] when none). */
  highlights: z.array(Highlight),
  /** 0018 — the parsed tags list ([] when none). */
  tags: z.array(z.string()),
  featured: z.boolean(),
  companionAnnouncementId: EntityId.nullable(),
  // Private-records slice (0011): the access level + a DERIVED lock flag; never pin/pin_hash (tenders.ts).
  accessLevel: RecordAccessLevel,
  isLocked: z.boolean(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  deletedAt: EpochMs.nullable(),
});
export type CmsEvent = z.infer<typeof CmsEvent>;
